import { environmentHttpClient } from "client/Api/httpClient";
import { formatForDto } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";

enum NedapOnsResourceKind {
	MyPlanning = 1,
	Clients = 2,
	MySchedule = 3
}

interface LiveInformationRequestModel {
	connectionId: number;
	date: string;
	interests: NedapOnsConnectionInterests;
}

export type NedapOnsConnectionInterests = {
	myPlanning?: true;
	mySchedule?: true;
};

enum ResultStatus {
	GeneralFailure = 0,
	Success = 1,
	NoAccess = 2
}

enum Gender {
	Unknown = 0,
	Male = 1,
	Female = 2
}

type Address = {
	street: string;
	number: string;
	numberPostfix: string;
	zipCode: string;
	city: string;
};

interface PlannedVisitListItem {
	clientId: number;
	clientIdentificationCode: string;
	clientName: string;
	clientInitials: string;
	clientGender: Gender;
	address: Address;
	calculatedStart: string;
	durationInSeconds: number;
	clientOverviewUrl: string;
}

interface ClientListItem {
	id: number;
	identificationCode: string;
	name: string;
	initials: string;
	gender: Gender;
	address: string;
	clientOverviewUrl: string;
}

export interface MyPlanningLiveInformationResponseModel {
	connectionId: number;
	planningDate: string;
	resultStatus: ResultStatus;
	items: PlannedVisitListItem[];
}

export type NedapOnsMyScheduleResponseModel = {
	status: ResultStatus;
	items: readonly NedapOnsEventListItem[];
};

type NedapOnsLiveInformationResponseModel = {
	connectionId: number;
	date: string;
	data: {
		myPlanning?: MyPlanningLiveInformationResponseModel;
		mySchedule?: NedapOnsMyScheduleResponseModel;
	};
};

function getLiveInformation(requests: LiveInformationRequestModel[]): Promise<NedapOnsLiveInformationResponseModel[]> {
	return environmentHttpClient.post<NedapOnsLiveInformationResponseModel[]>(
		"Workspace/LiveInformation/NedapOns/Get",
		requests
	);
}

function getClients(connectionId: number, skip: number, take: number, searchTerm: string): Promise<LazyLoadPageResult<ClientListItem>> {
	return environmentHttpClient.get<LazyLoadPageResult<ClientListItem>>(
		"Workspace/LiveInformation/NedapOns/GetClients",
		{
			connectionId,
			skip,
			take,
			searchTerm
		}
	);
}

export enum NedapOnsEventKind {
	Unknown = 0,
	Shift = 1,
	Unavailable = 2,
	Available = 3,
	Consulation = 4,
	Present = 5,
	Education = 6,
	Holidays = 7,
	LegalHolidays = 8,
	Illness = 9
}

export type NedapOnsShiftListItem = {
	id: number;
	kind: NedapOnsEventKind.Shift;
	start: string;
	end: string;
	title: string;
};

export type NedapOnsUnavailabilityListItem = {
	kind: Exclude<NedapOnsEventKind, NedapOnsEventKind.Shift>;
	start: string;
	end: string;
	title: string;
};

export type NedapOnsEventListItem =
	| NedapOnsShiftListItem
	| NedapOnsUnavailabilityListItem;

export type NedapOnsShiftDetails = {
	id: number;
	shiftId: string;
	team: string;
	code: string;
	description: string;
	expertiseProfile: string;
	start: string;
	end: string;
	duration: number;
};

export function getShiftDetails(
	connectionId: number,
	shiftAssignmentId: number,
	date: Date,
	timezone: TimeZone,
	signal?: AbortSignal
): Promise<NedapOnsShiftDetails> {
	return environmentHttpClient.get<NedapOnsShiftDetails>(
		"Workspace/LiveInformation/NedapOns/GetShiftDetails",
		{
			connectionId,
			shiftAssignmentId,
			date: formatForDto(date, timezone)
		},
		{
			signal
		}
	);
}

export {
	type NedapOnsLiveInformationResponseModel,
	type LiveInformationRequestModel as NedapOnsLiveInformationRequestModel,
	ResultStatus as NedapOnsResultStatus,
	NedapOnsResourceKind,
	type PlannedVisitListItem,
	type ClientListItem,
	getLiveInformation,
	getClients,
	Gender as NedapOnsGender,
	type Address
};